import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material/styles';
import { renderDateTimeLong } from 'utils/intl';
import vcatSpecialNotices from 'utils/vcatSpecialNotices.json';
import _ from 'lodash';
import DOMPurify from 'dompurify';

const marginBottom = { marginBottom: '30px', marginTop: '0px' };

const DetailsCard = styled(Card)({
  minWidth: 275,
  borderRadius: 8,
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
  background: '#fff',
  padding: 0,
  marginTop: 32,
  maxWidth: 600,
});

const CardHeader = styled('div')({
  fontWeight: 600,
  fontSize: 18,
  padding: '20px 24px 12px 24px',
  borderBottom: '1px solid #E0E0E0',
  background: '#fff',
});

const CardContentRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  padding: '24px',
  background: '#fff',
});

const Label = styled('div')({
  flex: 1,
  color: '#757575',
  fontSize: 15,
  marginRight: 24,
  minWidth: 200,
  lineHeight: 1.5,
});

const Value = styled('div')({
  flex: 2,
  color: '#212121',
  fontSize: 15,
  lineHeight: 1.5,
});

const WelcomeCounsellors = incomingProps => {
  const props = {
    ...{
      appointment: null,
    },
    ...incomingProps,
  };
  const { appointment } = props;
  let clientName = '';

  if (appointment) {
    clientName = get(appointment, 'client.preferred_name_with_last_name');

    if (
      appointment.provider_type === 'couples' &&
      appointment.couples_appointment_detail &&
      appointment.couples_appointment_detail.guest
    ) {
      clientName = `${clientName} and ${appointment.couples_appointment_detail.guest.full_name}`;
    }
  }

  const getSpecialNoticeString = useCallback(() => {
    const { client } = props;

    // we store vcat b2b user special notice to json file
    if (
      (_.get(client, 'dhe_id') ||
        _.get(client, 'dhe_profile_id') ||
        _.get(client, 'azure_profile_id')) &&
      _.get(client, 'company.name')
    ) {
      const companyName = _.get(client, 'company.name');
      const locale = localStorage.getItem('locale') || 'en';
      return vcatSpecialNotices[companyName]?.[locale];
    }

    if (_.get(client, 'company_user_type') === 'employee') {
      return _.get(client, 'company_info.special_notice_for_employees');
    }

    if (_.get(client, 'company_user_type') === 'dependant') {
      return _.get(client, 'company_info.special_notice_for_dependants');
    }
  }, [props.client]);

  return (
    <div>
      <h2 style={marginBottom}>
        <FormattedMessage defaultMessage="Welcome to the waiting room" />
      </h2>
      <h3 style={{ ...marginBottom, fontSize: '20px' }}>
        <FormattedMessage
          defaultMessage="Your session will begin shortly, with {name}"
          values={{ name: <b>{clientName}</b> }}
        />
      </h3>
      {appointment && appointment.provider_type === 'generic' ? (
        <p>
          <FormattedMessage defaultMessage="Please prepare any notes you may have" />
        </p>
      ) : (
        <p>
          <FormattedMessage defaultMessage="Please prepare any notes you may have, they are currently answering assessment questions." />
        </p>
      )}
      <hr style={{ margin: '0', marginBottom: '24px' }} className="line" />
      <Box>
        <DetailsCard variant="outlined">
          <CardHeader>
            <FormattedMessage defaultMessage="Appointment details" />
          </CardHeader>
          <Divider />
          <CardContentRow>
            <Label>
              {clientName && (
                <FormattedMessage
                  defaultMessage="
              Your client, {clientName}, has included the following appointment details for their session on {appointmentDate}"
                  values={{
                    clientName,
                    appointmentDate: renderDateTimeLong(
                      appointment?.start_date,
                    ),
                  }}
                />
              )}
            </Label>
            <Value>
              {appointment && appointment.description ? (
                <p>{appointment.description}</p>
              ) : (
                <FormattedMessage defaultMessage="No additional details provided by the client." />
              )}
            </Value>
          </CardContentRow>
        </DetailsCard>
        {getSpecialNoticeString()?.length > 0 && (
          <DetailsCard variant="outlined">
            <CardHeader>
              <FormattedMessage defaultMessage="Special Notice" />
            </CardHeader>
            <Divider />
            <CardContentRow>
              <Value>
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(getSpecialNoticeString()),
                  }}
                />
              </Value>
            </CardContentRow>
          </DetailsCard>
        )}
      </Box>
    </div>
  );
};

WelcomeCounsellors.propTypes = {
  appointment: PropTypes.object,
  client: PropTypes.object.isRequired,
};

export default WelcomeCounsellors;
